import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { cloneDeep } from 'lodash';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import filePlusIcon from 'assets/images/icons/file-plus.svg';
import formIcon from 'assets/images/icons/form-grey.svg';
import sectionIcon from 'assets/images/icons/section-grey.svg';
import AlertModal from 'components/common-components/AlertModal';
import DateTimeField from 'components/form-components/DateTimeField';
import FormButton from 'components/form-components/FormButton';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import NestedSelect from 'components/form-components/NestedSelect';
import NumberField from 'components/form-components/NumberField';
import RatingStarField from 'components/form-components/RatingStarField';
import SignatureField from 'components/form-components/SignatureField';
import SingleCheckBoxField from 'components/form-components/SingleCheckBoxField';
import TextArea from 'components/form-components/TextArea';
import TextField from 'components/form-components/TextField';
import {
  amendFieldIdentities,
  imageUrlToBase64,
  isShowField,
  listManagerSectionPermissions,
} from './utils';
import { isRTL, signatureStartingText } from 'config/config';
import { CallAPI } from 'actions/General';

const AddSubmission = ({
  selectedRow,
  handleClose,
  onSubmitForm,
  supervisorList,
  loading,
  isNestedModal,
  setShowSupervisorLockedModal,
}) => {
  const { t } = useTranslation();
  const authProfile = useSelector((state) => state.auth.profile);
  const currentUserId = authProfile.userCompanyId;
  const currentUserRole = authProfile.role;

  const [formSectionSupervisorFields, setFormSectionSupervisorFields] = useState([]);
  const [formValues, setFormValues] = useState(null);
  const [elementGlobalIdTypeMap, setElementGlobalIdTypeMap] = useState({});
  const [formName, setFormName] = useState('');
  const [formDirection, setFormDirection] = useState('');
  const [notifyViaSms, setNotifyViaSms] = useState({});
  const [fieldsNotes, setFieldsNotes] = useState({});
  const [openedNotesModal, setOpenedNotesModal] = useState(null);
  const [expandedNoteField, setExpandedNoteField] = useState(null);
  const [anyRequiredFieldUnfilled, setAnyRequiredFieldUnfilled] = useState(false);
  const [updatedFields, setUpdatedFields] = useState({});

  const getSupervisorFields = async () => {
    const apiPath = selectedRow.formId + '/' + selectedRow.form_version_id;
    const result = await CallAPI('GET_FORM_VERSION_DETAIL', null, apiPath, null, null, null);
    if (result.status) {
      setFormName(result.data.title);
      setFormDirection(result.data.textDirection);
      const fieldList = [];

      let sectionTitle = '';
      let sectionVisibility = {};
      let fieldIdentitiesObj = {};
      let idTypeMap = {};

      let supervisorElementsList = result.data.versions[0].supervisorElements ?? [];
      // first sort data in ascending order w.r.t "position" property in each object
      supervisorElementsList = supervisorElementsList.sort((a, b) => a.position - b.position);

      const allManagerSectionElements = [];

      supervisorElementsList.forEach((ele) => {
        if (ele.elementType === 'section') allManagerSectionElements.push(ele);
        else {
          ele.sectionGlobalId =
            allManagerSectionElements.find((section) => section.sectionNumber === ele.sectionNumber)
              ?.elementGlobalId ?? allManagerSectionElements[0].elementGlobalId;
          idTypeMap[ele.elementGlobalId] = ele.elementType;
        }
      });
      setElementGlobalIdTypeMap({ ...idTypeMap });

      const userMangerSectionPermissions = listManagerSectionPermissions(
        allManagerSectionElements,
        result.data.managerSections ?? [],
        currentUserRole,
        currentUserId,
      );

      supervisorElementsList.map((element) => {
        let elementType = element.elementType;
        const formSupervisorProperties = JSON.parse(element.properties);

        amendFieldIdentities(
          fieldIdentitiesObj,
          element,
          formSupervisorProperties.basicProperties.fieldTitle,
        );

        if (elementType === 'section') {
          sectionTitle = formSupervisorProperties?.basicProperties?.sectionTitle;
          sectionVisibility[sectionTitle] = formSupervisorProperties.validationProperties.hidden;
        } else {
          // Finding all permissions for this field section
          const foundPermissions = userMangerSectionPermissions.filter(
            (permission) => permission.managerSectionGlobalId === element.sectionGlobalId,
          );
          if (!foundPermissions.length) return;

          // Now check permission by priority of access level
          const priorityPermission =
            foundPermissions.find((permission) => permission.accessLevel === 'full_access') ||
            foundPermissions.find((permission) => permission.accessLevel === 'view_only');

          let additionalDetail = {};
          if (elementType === 'dateTime')
            elementType = formSupervisorProperties.basicProperties.subType;
          else if (elementType === 'status')
            additionalDetail = {
              optionList: [
                { responseText: 'Select Option', value: '' },
                ...formSupervisorProperties.basicProperties.optionList,
              ],
            };
          else if (elementType === 'response')
            additionalDetail = {
              optionList: [
                { responseText: 'Select Option', value: '' },
                ...formSupervisorProperties.basicProperties.optionList,
              ],
            };
          else if (elementType === 'ratingSlider')
            additionalDetail = {
              noOfStars: formSupervisorProperties.basicProperties.ratingPointers,
            };

          let displayName = '';
          let hintText = '';
          if (formSupervisorProperties?.otherProperties?.shortTitle) {
            displayName = formSupervisorProperties.otherProperties.shortTitle;
            hintText = formSupervisorProperties.otherProperties.fieldTitle;
          } else displayName = formSupervisorProperties.basicProperties.fieldTitle;
          let headerSupervisorProp = {
            name: element.elementGlobalId,
            displayName,
            type: elementType,
            associatedKey: element.elementGlobalId,
            additionalDetail: additionalDetail,
            hintText,
            sectionTitle,
            hidden:
              formSupervisorProperties.validationProperties.hidden ||
              sectionVisibility[sectionTitle],
            fieldIdentitiesObj: fieldIdentitiesObj,
            properties: {
              mediaProperties: formSupervisorProperties.mediaProperties,
              validationProperties: formSupervisorProperties.validationProperties,
            },
            ...priorityPermission,
          };

          fieldList.push(headerSupervisorProp);
        }
      });

      const allowedSupervisorFieldInCurrentState = fieldList.find(
        (singleHeaderField) =>
          singleHeaderField.managerSectionGlobalId === selectedRow.submissionFlowState.actionId,
      );

      if (allowedSupervisorFieldInCurrentState) {
        if (allowedSupervisorFieldInCurrentState.accessLevel !== 'full_access') {
          toast.error('You do not have permission to edit this section');
          handleClose();
          return;
        }
        // If comes here, the flow continues
      } else {
        setShowSupervisorLockedModal(selectedRow.submissionFlowState.flowState);
        return;
      }

      const filteredSectionFields = filterAllowedSectionFields(cloneDeep(fieldList));
      setFormSectionSupervisorFields(filteredSectionFields);
    }
  };

  const filterAllowedSectionFields = (sectionSupervisorFields) => {
    const fieldList = [];
    for (let index = 0; index < sectionSupervisorFields.length; index++) {
      const singleColumn = sectionSupervisorFields[index];
      const submissionFlowState = selectedRow.submissionFlowState;
      if (
        submissionFlowState.actionId &&
        submissionFlowState.actionId === singleColumn.managerSectionGlobalId &&
        singleColumn.accessLevel === 'full_access'
      ) {
        fieldList.push(singleColumn);
      }
    }
    return fieldList;
  };

  const prepareFormData = async (sectionSupervisorFields) => {
    const currentSectionId = selectedRow.submissionFlowState.actionId;
    if (sectionSupervisorFields.length) {
      const signatureMediaUrlsMap = await fetchSignatureMediaUrls(
        selectedRow,
        elementGlobalIdTypeMap,
      );

      let values = {
        submissionId: selectedRow.form_submission_id,
        formVersionId: selectedRow.form_version_id,
        mediaCustomFolderId: selectedRow.media_custom_folder_id,
        isAnyFieldMissing: false,
        direction: formDirection,
        mediaNotes: selectedRow.mediaNotes,
        mediaObj: selectedRow.mediaObj,
        elementMetaDataMap: selectedRow.elementMetaDataMap,
      };

      // Just to show message if comes from a different from version
      supervisorList.forEach((singleColumn) => {
        if (
          !sectionSupervisorFields.find((field) => field.name === singleColumn.name) &&
          singleColumn.managerSectionGlobalId === currentSectionId
        )
          values.isAnyFieldMissing = true;
      });

      for (let index = 0; index < sectionSupervisorFields.length; index++) {
        const singleColumn = sectionSupervisorFields[index];

        values[singleColumn.name] =
          selectedRow[singleColumn.name] && singleColumn.type !== 'signature'
            ? selectedRow[singleColumn.name]
            : '';

        if (singleColumn.type === 'signature' && signatureMediaUrlsMap[singleColumn.name]) {
          const imgBase64 = await imageUrlToBase64(signatureMediaUrlsMap[singleColumn.name]);
          values[singleColumn.name] = imgBase64 ?? '';
        }
      }

      setFormValues(values);
    }
  };

  const prepareDataAndSubmit = async () => {
    let sendNotificationToInspector = false;

    const formData = [];
    let requiredFieldUnfilled = false;
    Object.entries(formValues).forEach(([key, value]) => {
      const foundSupervisorField = formSectionSupervisorFields.find((field) => field.name === key);
      if (foundSupervisorField) {
        let newObject = {
          elementGlobalId: key,
          valueField: value.toString(),
          media: formValues.mediaObj[key] || [],
          notesField: fieldsNotes[key] || '',
          arrayField: [],
        };

        if (foundSupervisorField.type === 'signature') {
          newObject.type = 'signature';
          newObject.elementMetaData = formValues.elementMetaDataMap[key];
        } else if (foundSupervisorField.type === 'response') {
          const selectedValueOption = foundSupervisorField.additionalDetail.optionList.find(
            (option) => option.value === value && value !== '',
          );
          if (selectedValueOption)
            newObject.elementMetaData = JSON.stringify({
              displayMap: { [value]: selectedValueOption.responseText },
            });
        }
        if (isShowField(formValues, foundSupervisorField)) {
          if (
            !requiredFieldUnfilled &&
            !value &&
            foundSupervisorField.properties.validationProperties.required
          ) {
            requiredFieldUnfilled = true;
          }
          formData.push(newObject);
          if (!sendNotificationToInspector && notifyViaSms[`notify_${key}`])
            sendNotificationToInspector = true;
        }
      }
    });

    if (requiredFieldUnfilled) {
      setAnyRequiredFieldUnfilled(true);
    } else {
      onSubmitForm(
        formData,
        formValues.submissionId,
        formValues.formVersionId,
        formSectionSupervisorFields[0].managerSectionGlobalId ?? null,
        formValues.mediaCustomFolderId,
        sendNotificationToInspector,
        updatedFields,
      );
    }
  };

  const getBulkImageUrls = async (bulkMediaList) => {
    if (bulkMediaList.length === 0) return {};
    let result = await CallAPI(
      'GET_BULK_DOWNLOAD_MEDIA_REDIRECT_URL',
      { medias: bulkMediaList },
      null,
      null,
      null,
    );
    if (result.status) {
      const mediaUrlMap = Object.fromEntries(result.data.map((media) => [media._id, media.url]));
      return mediaUrlMap;
    } else {
      return {};
    }
  };

  const fetchSignatureMediaUrls = async (singleSubmission, idsTypeMap) => {
    const bulkMediaPayload = [];

    for (let valueIndex = 0; valueIndex < singleSubmission.values.length; valueIndex++) {
      const singleFormEntry = singleSubmission.values[valueIndex];

      if (idsTypeMap[singleFormEntry.elementGlobalId] === 'signature') {
        let media = singleFormEntry.media;
        for (let mediaIndex = 0; mediaIndex < media.length; mediaIndex++) {
          const singleMedia = media[mediaIndex];
          if (
            (singleMedia && singleMedia.imageName.indexOf(signatureStartingText) === 0) ||
            singleFormEntry.elementMetaData?.includes(singleMedia._id)
          ) {
            bulkMediaPayload.push({
              mediaType: 'image',
              imageName: singleMedia.imageName,
              formId: singleSubmission.form,
              versionId: singleSubmission.formVersion,
              customFolderId: singleMedia.customFolderId,
              mediaId: singleMedia._id,
            });
          }
        }
      }
    }

    const mediaUrlMap = await getBulkImageUrls(bulkMediaPayload);
    const signatureMediaUrlsMap = {};

    for (let valueIndex = 0; valueIndex < singleSubmission.values.length; valueIndex++) {
      const singleFormEntry = singleSubmission.values[valueIndex];

      if (idsTypeMap[singleFormEntry.elementGlobalId] === 'signature') {
        let media = singleFormEntry.media;
        for (let mediaIndex = 0; mediaIndex < media.length; mediaIndex++) {
          const singleMedia = media[mediaIndex];
          if (
            (singleMedia && singleMedia.imageName.indexOf(signatureStartingText) === 0) ||
            singleFormEntry.elementMetaData?.includes(singleMedia._id)
          ) {
            signatureMediaUrlsMap[singleFormEntry.elementGlobalId] =
              mediaUrlMap[singleMedia._id] ?? null;
          }
        }
      }
    }

    return signatureMediaUrlsMap;
  };

  const handleValuesChange = (field, value) => {
    setFormValues({ ...formValues, [field]: value });
    setAnyRequiredFieldUnfilled(false);
    setUpdatedFields((prevState) => ({ ...prevState, [field]: true }));
  };

  const handleFieldsNotes = (field, value) => {
    setFieldsNotes((prevState) => ({ ...prevState, [field]: value }));
  };

  const handleNotifySmsHandleChange = (field, value) => {
    setNotifyViaSms({ ...notifyViaSms, [field]: value });
  };

  const getFieldOptionList = (field) => {
    return (field.additionalDetail?.optionList ?? []).map((option) => ({
      displayValue: option.responseText,
      value: option.value,
    }));
  };

  const getSelectedOptionLabel = (field) => {
    const selectedOption = (field.additionalDetail?.optionList ?? []).find(
      (option) => option.value === formValues[field.name],
    );
    return selectedOption?.responseText ?? 'select_select';
  };

  const isNoteLong = (fieldName) => fieldsNotes[fieldName].length > 40;

  const toggleNote = (fieldName) => {
    setExpandedNoteField((prev) => (prev === fieldName ? null : fieldName));
  };

  useEffect(() => {
    getSupervisorFields();
  }, []);

  useEffect(() => {
    if (formSectionSupervisorFields.length) prepareFormData(formSectionSupervisorFields);
  }, [formSectionSupervisorFields]);

  return !formValues ? (
    <></>
  ) : (
    <AlertModal
      show={true}
      closeModal={handleClose}
      actionButtons={[]}
      noPadding
      backdropClassName={isNestedModal ? 'backdrop-overlap' : ''}
    >
      <div className='add-submission-modal py-4'>
        <div className='px-4'>
          <label className='w-100 theme-text-black-1 text-center theme-font-inter-semi-bold theme-size-1_5 mb-2 border-bottom-grey-1-h-1 pb-2'>
            {t('text_update_submission')}
          </label>
          {formValues.isAnyFieldMissing && (
            <label className='w-100 theme-text-red-1 text-center'>
              {t('text_field_not_contains_in_version')}
            </label>
          )}

          <div className='d-flex gap-3 align-items-center theme-font-inter-medium mt-2'>
            <img src={formIcon} alt='Form title icon' />
            {formName}
          </div>
          <div className='d-flex gap-3 align-items-center theme-font-inter-medium mt-2'>
            <img src={sectionIcon} alt='Section name icon' />
            {formSectionSupervisorFields[0]?.sectionTitle ?? ''}
          </div>
        </div>

        {formValues && (
          <div className='d-flex flex-column gap-2 align-items-center'>
            <div className='fields-list-section py-2 px-4'>
              {formSectionSupervisorFields.map((singleField, index) => {
                const isDisabled = singleField.properties.validationProperties.readOnly ?? false;
                return isShowField(formValues, singleField) && !singleField.hidden ? (
                  <div key={index} className='field-data border-grey-1-h-1'>
                    {singleField.type === 'response' || singleField.type === 'status' ? (
                      <NestedSelect
                        options={getFieldOptionList(singleField)}
                        name={singleField.name}
                        displayName={singleField.displayName}
                        required={singleField.properties.validationProperties.required}
                        handleChange={handleValuesChange}
                        toggleClasses='p-2 border-grey-1-h-1'
                        toggleComponent={
                          <>
                            <label className='cursor-pointer'>
                              {getSelectedOptionLabel(singleField)}
                            </label>
                            <img src={chevronGreyIcon} className='rotate-180' />
                          </>
                        }
                        align={isRTL() ? 'end' : 'start'}
                        disabled={isDisabled}
                      />
                    ) : singleField.type === 'dateAndTime' ||
                      singleField.type === 'date' ||
                      singleField.type === 'time' ? (
                      <DateTimeField
                        value={
                          formValues[singleField.name]
                            ? formValues[singleField.name].toString()
                            : ''
                        }
                        name={singleField.name}
                        placeholder=''
                        type={singleField.type}
                        displayName={singleField.displayName}
                        required={singleField.properties.validationProperties.required}
                        classes='min-width-160'
                        handleChange={handleValuesChange}
                        unixTimeStamp
                        disabled={isDisabled}
                      />
                    ) : singleField.type === 'number' ? (
                      <NumberField
                        value={
                          formValues[singleField.name]
                            ? parseInt(formValues[singleField.name])
                            : undefined
                        }
                        name={singleField.name}
                        displayName={singleField.displayName}
                        required={singleField.properties.validationProperties.required}
                        classes='min-width-160'
                        handleChange={handleValuesChange}
                        disabled={isDisabled}
                        returnString
                      />
                    ) : singleField.type === 'ratingSlider' ? (
                      <RatingStarField
                        value={parseInt(formValues[singleField.name])}
                        displayName={singleField.displayName}
                        required={singleField.properties.validationProperties.required}
                        numOfStars={singleField.additionalDetail.noOfStars}
                        handleChange={(value) => handleValuesChange(singleField.name, value)}
                        disabled={isDisabled}
                      />
                    ) : singleField.type === 'signature' ? (
                      <SignatureField
                        name={singleField.name}
                        displayName={singleField.displayName}
                        required={singleField.properties.validationProperties.required}
                        onChange={(value) => handleValuesChange(singleField.name, value)}
                        existingSignature={formValues[singleField.name]}
                      />
                    ) : (
                      <Fragment>
                        <TextField
                          value={formValues[singleField.name]}
                          name={singleField.name}
                          displayName={singleField.displayName}
                          required={singleField.properties.validationProperties.required}
                          placeholder={singleField.displayName}
                          classes='mt-1'
                          handleChange={handleValuesChange}
                          disabled={isDisabled}
                        />
                        <SingleCheckBoxField
                          name={`notify_${singleField.name}`}
                          value={notifyViaSms[`notify_${singleField.name}`] ?? false}
                          handleChange={handleNotifySmsHandleChange}
                          text={<span>{t('form_field_notify_via_sms')}</span>}
                          disabled={isDisabled}
                        />
                      </Fragment>
                    )}

                    {singleField.properties.mediaProperties?.allowNotes && (
                      <>
                        {fieldsNotes[singleField.name] && (
                          <div>
                            <div className='d-flex gap-2'>
                              <strong>{t('text_note_added')}: </strong>
                              <div>
                                {isNoteLong(singleField.name) &&
                                expandedNoteField !== singleField.name ? (
                                  <>
                                    {fieldsNotes[singleField.name].slice(0, 40)}...
                                    <button
                                      onClick={() => toggleNote(singleField.name)}
                                      className='btn btn-link p-0 ms-1 theme-size-0_9'
                                    >
                                      {t('text_more').toLowerCase()}
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    {fieldsNotes[singleField.name]}
                                    {isNoteLong(singleField.name) && (
                                      <button
                                        onClick={() => toggleNote(singleField.name)}
                                        className='btn btn-link p-0 ms-1 theme-size-0_9'
                                      >
                                        {t('text_hide').toLowerCase()}
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        <div
                          className={`d-flex align-items-center gap-4 ${
                            isDisabled ? 'disabled' : ''
                          }`}
                        >
                          <div className='d-flex align-items-center gap-2'>
                            <label className='theme-text-black-1'>{t('text_add')} :</label>
                            <div className='vertical-line'></div>
                          </div>
                          <div
                            className='d-flex align-items-center gap-2 cursor-pointer'
                            onClick={() => {
                              setOpenedNotesModal({
                                fieldName: singleField.name,
                                fieldValue: fieldsNotes[singleField.name] || '',
                              });
                            }}
                          >
                            <img src={filePlusIcon} />
                            <label className='theme-text-black-1 cursor-pointer'>
                              {t('text_note')}
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : null;
              })}
            </div>

            {openedNotesModal && (
              <NotesModal
                openedNotesModal={openedNotesModal}
                setOpenedNotesModal={setOpenedNotesModal}
                handleFieldsNotes={handleFieldsNotes}
              />
            )}
            {anyRequiredFieldUnfilled && (
              <span className='align-self-start theme-text-red-1 px-4'>
                {t('error_all_required_fields_must_be_filled')}
              </span>
            )}
            <div className='w-100 d-flex gap-3 px-4'>
              <FormButton
                classes='w-100'
                text='button_cancel'
                variant='white-1'
                onClick={handleClose}
              />
              <FormLoadingButton
                classes='w-100'
                onClick={prepareDataAndSubmit}
                loading={loading}
                text='button_submit'
                variant='green-1'
                disabled={formSectionSupervisorFields.length === 0}
              />
            </div>
          </div>
        )}
      </div>
    </AlertModal>
  );
};

AddSubmission.propTypes = {
  selectedRow: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSubmitForm: PropTypes.func.isRequired,
  supervisorList: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  isNestedModal: PropTypes.bool,
  setShowSupervisorLockedModal: PropTypes.func.isRequired,
};

export default AddSubmission;

const NotesModal = ({ openedNotesModal, setOpenedNotesModal, handleFieldsNotes }) => {
  const [fieldNote, setFieldNote] = useState('');
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const maxCharacterLimit = 500;
  const handleNote = (_, value) => {
    let text = value;
    const isExceedingLimit = text.length > maxCharacterLimit;
    if (isExceedingLimit) {
      text = value.slice(0, maxCharacterLimit);
    }
    setError(isExceedingLimit ? 'max character limit' : null);
    setFieldNote(text);
  };

  useEffect(() => {
    if (openedNotesModal.fieldValue) {
      setFieldNote(openedNotesModal.fieldValue);
    }
  }, [openedNotesModal.fieldValue]);

  return (
    <AlertModal show={!!openedNotesModal} actionButtons={[]} backdropClassName={'backdrop-overlap'}>
      <>
        <label className='theme-text-black-1 theme-font-inter-semi-bold theme-size-1_5'>
          {t('text_add_note')}
        </label>
        <TextArea
          name={openedNotesModal.fieldName}
          value={fieldNote}
          placeholder='text_enter_note_here'
          classes='w-100 mb-3'
          handleChange={handleNote}
          rows={4}
          error={error}
        />
        <div className='d-flex w-100 gap-2 justify-content-center'>
          <FormButton
            text={t('button_cancel')}
            variant='white-1'
            onClick={() => setOpenedNotesModal(null)}
          />
          <FormLoadingButton
            text='button_save'
            variant='green-1'
            onClick={() => {
              handleFieldsNotes(openedNotesModal.fieldName, fieldNote);
              setOpenedNotesModal(null);
            }}
            loading={false}
          />
        </div>
      </>
    </AlertModal>
  );
};

NotesModal.propTypes = {
  openedNotesModal: PropTypes.object.isRequired,
  setOpenedNotesModal: PropTypes.func.isRequired,
  handleFieldsNotes: PropTypes.func.isRequired,
};
